/**
 * Types and pure functions for mosaic layout drag constraints.
 *
 * When dragging a widget, it should not overlap other widgets.
 * At drag start, we precompute free rectangles using grid decomposition.
 * During drag, we find the closest free rectangle and clamp within it.
 */
import { MIN_SIZE, snapToMosaicGrid } from './mosaic-drop-targets';
import type { NormalizedLayout } from './mosaic-drop-targets';

export interface ShrinkablePosition {
    x: number;
    y: number;
    w: number; // May be smaller than original if shrinking
    h: number; // May be smaller than original if shrinking
    shrunk: boolean; // True if size was reduced
    clamped: boolean; // True if position was shifted to fit within free rect at full size
}

export interface FreeRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface FreeSpaceState {
    freeRects: FreeRect[];
    otherWidgets: NormalizedLayout[];
    draggedWidgetSize: { w: number; h: number };
}

/**
 * Collect unique x and y coordinates from widget edges plus viewport boundaries.
 * These form a grid where each cell is either fully occupied or fully empty.
 */
export function collectGridLines(layouts: NormalizedLayout[]): { xLines: number[]; yLines: number[] } {
    const xSet = new Set<number>([0, 1]);
    const ySet = new Set<number>([0, 1]);

    for (const layout of layouts) {
        xSet.add(layout.x);
        xSet.add(layout.x + layout.w);
        ySet.add(layout.y);
        ySet.add(layout.y + layout.h);
    }

    return {
        xLines: [...xSet].sort((a, b) => a - b),
        yLines: [...ySet].sort((a, b) => a - b),
    };
}

/**
 * Build occupancy grid marking which cells are occupied by widgets.
 */
export function buildOccupancyGrid(layouts: NormalizedLayout[], xLines: number[], yLines: number[]): boolean[][] {
    const rows = yLines.length - 1;
    const cols = xLines.length - 1;
    const grid: boolean[][] = [];

    for (let r = 0; r < rows; r++) {
        grid[r] = [];
        for (let c = 0; c < cols; c++) {
            grid[r][c] = false;
        }
    }

    for (const layout of layouts) {
        for (let row = 0; row < rows; row++) {
            if (yLines[row + 1] <= layout.y || yLines[row] >= layout.y + layout.h) continue;
            for (let col = 0; col < cols; col++) {
                if (xLines[col + 1] <= layout.x || xLines[col] >= layout.x + layout.w) continue;
                grid[row][col] = true;
            }
        }
    }

    return grid;
}

const EPSILON = 1e-9;

/**
 * Check if rectangle A is fully enclosed within rectangle B.
 */
function isEnclosedIn(a: FreeRect, b: FreeRect): boolean {
    return (
        a.x >= b.x - EPSILON &&
        a.y >= b.y - EPSILON &&
        a.x + a.w <= b.x + b.w + EPSILON &&
        a.y + a.h <= b.y + b.h + EPSILON
    );
}

/**
 * Filter out rectangles that are fully enclosed by another rectangle.
 */
function filterEnclosedRectangles(rects: FreeRect[]): FreeRect[] {
    return rects.filter((rect) => {
        for (const other of rects) {
            if (rect === other) continue;
            if (isEnclosedIn(rect, other) && (other.w > rect.w + EPSILON || other.h > rect.h + EPSILON)) {
                return false;
            }
        }
        return true;
    });
}

/**
 * Find maximal free rectangles by expanding in all 4 diagonal directions.
 *
 * For each unoccupied cell, we expand to maximal rectangles in all directions:
 * - Right-Down, Left-Down, Right-Up, Left-Up
 * This ensures we find all maximal rectangles, not just those anchored at top-left.
 * Rectangles are allowed to overlap. Duplicates and enclosed rectangles are filtered out.
 */
export function findMaximalFreeRectangles(grid: boolean[][], xLines: number[], yLines: number[]): FreeRect[] {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    if (rows === 0 || cols === 0) return [];

    const seen = new Set<string>();
    const rectangles: FreeRect[] = [];

    function addRect(minCol: number, minRow: number, maxCol: number, maxRow: number) {
        const x = xLines[minCol];
        const y = yLines[minRow];
        const w = xLines[maxCol + 1] - x;
        const h = yLines[maxRow + 1] - y;
        // Round to 6 decimal places for deduplication to handle floating point precision
        const key = `${x.toFixed(6)},${y.toFixed(6)},${w.toFixed(6)},${h.toFixed(6)}`;
        if (!seen.has(key)) {
            seen.add(key);
            rectangles.push({ x, y, w, h });
        }
    }

    for (let startRow = 0; startRow < rows; startRow++) {
        for (let startCol = 0; startCol < cols; startCol++) {
            if (grid[startRow][startCol]) continue;

            // Direction 1: Expand Right-Down (anchor at top-left)
            {
                let maxCol = cols - 1;
                for (let row = startRow; row < rows; row++) {
                    let col = startCol;
                    while (col <= maxCol && !grid[row][col]) col++;
                    if (col <= startCol) break;
                    maxCol = col - 1;
                    addRect(startCol, startRow, maxCol, row);
                }
            }

            // Direction 2: Expand Left-Down (anchor at top-right)
            {
                let minCol = 0;
                for (let row = startRow; row < rows; row++) {
                    let col = startCol;
                    while (col >= minCol && !grid[row][col]) col--;
                    if (col >= startCol) break;
                    minCol = col + 1;
                    addRect(minCol, startRow, startCol, row);
                }
            }

            // Direction 3: Expand Right-Up (anchor at bottom-left)
            {
                let maxCol = cols - 1;
                for (let row = startRow; row >= 0; row--) {
                    let col = startCol;
                    while (col <= maxCol && !grid[row][col]) col++;
                    if (col <= startCol) break;
                    maxCol = col - 1;
                    addRect(startCol, row, maxCol, startRow);
                }
            }

            // Direction 4: Expand Left-Up (anchor at bottom-right)
            {
                let minCol = 0;
                for (let row = startRow; row >= 0; row--) {
                    let col = startCol;
                    while (col >= minCol && !grid[row][col]) col--;
                    if (col >= startCol) break;
                    minCol = col + 1;
                    addRect(minCol, row, startCol, startRow);
                }
            }
        }
    }

    return filterEnclosedRectangles(rectangles);
}

/**
 * Compute free space state for drag constraints.
 * Excludes the dragged widget from occupancy.
 */
export function computeFreeSpace(
    layouts: Record<string, NormalizedLayout>,
    draggedContainerId: string,
): FreeSpaceState {
    const draggedLayout = layouts[draggedContainerId];
    if (!draggedLayout) {
        return { freeRects: [], otherWidgets: [], draggedWidgetSize: { w: 0, h: 0 } };
    }

    const otherWidgets: NormalizedLayout[] = [];
    for (const [id, layout] of Object.entries(layouts)) {
        if (id !== draggedContainerId) {
            otherWidgets.push(layout);
        }
    }

    const { xLines, yLines } = collectGridLines(otherWidgets);
    const grid = buildOccupancyGrid(otherWidgets, xLines, yLines);
    const freeRects = findMaximalFreeRectangles(grid, xLines, yLines);

    return {
        freeRects,
        otherWidgets,
        draggedWidgetSize: { w: draggedLayout.w, h: draggedLayout.h },
    };
}

/**
 * Find all free rectangles that contain the cursor position.
 * Multiple free rects can contain the cursor since maximal rects can overlap.
 */
export function findFreeRectsUnderCursor(mouseX: number, mouseY: number, freeRects: FreeRect[]): FreeRect[] {
    const result: FreeRect[] = [];

    for (const rect of freeRects) {
        if (mouseX >= rect.x && mouseX < rect.x + rect.w && mouseY >= rect.y && mouseY < rect.y + rect.h) {
            result.push(rect);
        }
    }

    return result;
}

/**
 * Compute the Euclidean distance from a point to the nearest edge of a rectangle.
 * Returns 0 if the point is inside the rectangle.
 */
export function distanceToRect(px: number, py: number, rect: FreeRect): number {
    const dx = Math.max(rect.x - px, 0, px - (rect.x + rect.w));
    const dy = Math.max(rect.y - py, 0, py - (rect.y + rect.h));
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Find the nearest free rect to the cursor position, within a maximum search distance.
 * Used when the cursor is over occupied space to find nearby free space.
 * Only considers rects that are at least MIN_SIZE in both dimensions.
 */
export function findNearestFreeRect(
    mouseX: number,
    mouseY: number,
    freeRects: FreeRect[],
    maxDistance: number,
): FreeRect | null {
    let bestRect: FreeRect | null = null;
    let bestDistance = maxDistance;

    for (const rect of freeRects) {
        if (rect.w < MIN_SIZE - EPSILON || rect.h < MIN_SIZE - EPSILON) {
            continue;
        }
        const dist = distanceToRect(mouseX, mouseY, rect);
        if (dist < bestDistance) {
            bestDistance = dist;
            bestRect = rect;
        }
    }

    return bestRect;
}

/**
 * Check if a widget placed at (x, y) with size (w, h) fits entirely within a rect.
 */
function widgetFitsInRect(x: number, y: number, w: number, h: number, rect: FreeRect): boolean {
    return (
        x >= rect.x - EPSILON &&
        y >= rect.y - EPSILON &&
        x + w <= rect.x + rect.w + EPSILON &&
        y + h <= rect.y + rect.h + EPSILON
    );
}

// Reusable result objects
const constrainResult = { x: 0, y: 0 };
const shrinkableResult: ShrinkablePosition = { x: 0, y: 0, w: 0, h: 0, shrunk: false, clamped: false };

/**
 * Clamp widget position within a free rectangle.
 */
export function clampToFreeRect(
    targetX: number,
    targetY: number,
    widgetW: number,
    widgetH: number,
    rect: FreeRect,
): { x: number; y: number } {
    constrainResult.x = Math.max(rect.x, Math.min(targetX, rect.x + rect.w - widgetW));
    constrainResult.y = Math.max(rect.y, Math.min(targetY, rect.y + rect.h - widgetH));
    return constrainResult;
}

// Aliases for compatibility
export type DragConstraintState = FreeSpaceState;
export const initDragConstraints = computeFreeSpace;

/**
 * Constrain drag position to avoid collisions, with shrink-to-fit support.
 *
 * The cursor position determines which free rects are candidates.
 * The target position is where the widget would be placed (top-left corner).
 *
 * Logic:
 * 1. Find all free rects that contain the cursor
 * 2. Check if widget at TARGET position fits entirely within ANY of those rects
 *    - If yes → normal move (not blocked, not shrunk)
 * 3. If widget doesn't fit at target, find best shrink option among rects under cursor
 *    - If found → shrink preview
 * 4. If no valid option → blocked (return null)
 *
 * @param targetX - Desired X position for widget top-left
 * @param targetY - Desired Y position for widget top-left
 * @param mouseX - Current cursor X position (normalized 0-1)
 * @param mouseY - Current cursor Y position (normalized 0-1)
 * @param state - Precomputed free space state
 * @returns ShrinkablePosition with x, y, w, h, and shrunk flag, or null if blocked
 */
export function constrainDragPosition(
    targetX: number,
    targetY: number,
    mouseX: number,
    mouseY: number,
    state: FreeSpaceState,
): ShrinkablePosition | null {
    const { w, h } = state.draggedWidgetSize;
    const { freeRects } = state;

    // Step 1: Find all free rects under the cursor
    const rectsUnderCursor = findFreeRectsUnderCursor(mouseX, mouseY, freeRects);

    if (rectsUnderCursor.length === 0) {
        // Cursor is over occupied space — try to find nearest free rect
        const maxDistance = Math.sqrt(w * w + h * h);
        const nearestRect = findNearestFreeRect(mouseX, mouseY, freeRects, maxDistance);

        if (!nearestRect) {
            return null; // No free rect nearby — truly blocked
        }

        // Try to place widget in the nearest rect
        if (nearestRect.w >= w - EPSILON && nearestRect.h >= h - EPSILON) {
            // Full size fits — clamp to nearest rect
            const clamped = clampToFreeRect(targetX, targetY, w, h, nearestRect);
            shrinkableResult.x = snapToMosaicGrid(clamped.x);
            shrinkableResult.y = snapToMosaicGrid(clamped.y);
            shrinkableResult.w = w;
            shrinkableResult.h = h;
            shrinkableResult.shrunk = false;
            shrinkableResult.clamped = true;
            return shrinkableResult;
        }

        // Need to shrink to fit the nearest rect
        const shrunkW = snapToMosaicGrid(Math.max(MIN_SIZE, Math.min(nearestRect.w, w)));
        const shrunkH = snapToMosaicGrid(Math.max(MIN_SIZE, Math.min(nearestRect.h, h)));

        if (shrunkW < MIN_SIZE - EPSILON || shrunkH < MIN_SIZE - EPSILON) {
            return null; // Rect too small even for minimum size
        }

        // Position using proportional grab offset
        const grabOffsetX = mouseX - targetX;
        const grabOffsetY = mouseY - targetY;
        const grabRatioX = Math.max(0, Math.min(1, grabOffsetX / w));
        const grabRatioY = Math.max(0, Math.min(1, grabOffsetY / h));
        const scaledOffsetX = grabRatioX * shrunkW;
        const scaledOffsetY = grabRatioY * shrunkH;

        const rawX = Math.max(nearestRect.x, Math.min(mouseX - scaledOffsetX, nearestRect.x + nearestRect.w - shrunkW));
        const rawY = Math.max(nearestRect.y, Math.min(mouseY - scaledOffsetY, nearestRect.y + nearestRect.h - shrunkH));

        shrinkableResult.x = snapToMosaicGrid(rawX);
        shrinkableResult.y = snapToMosaicGrid(rawY);
        shrinkableResult.w = shrunkW;
        shrinkableResult.h = shrunkH;
        shrinkableResult.shrunk = shrunkW < w - EPSILON || shrunkH < h - EPSILON;
        shrinkableResult.clamped = !shrinkableResult.shrunk;
        return shrinkableResult;
    }

    // Step 2: Check if widget at TARGET position fits entirely within ANY rect under cursor
    // This is the key check - even if cursor is in free space, the widget might extend
    // outside that free space and collide with other widgets
    for (const rect of rectsUnderCursor) {
        if (widgetFitsInRect(targetX, targetY, w, h, rect)) {
            // Widget fits at target position within this rect - normal move
            shrinkableResult.x = targetX;
            shrinkableResult.y = targetY;
            shrinkableResult.w = w;
            shrinkableResult.h = h;
            shrinkableResult.shrunk = false;
            shrinkableResult.clamped = false;
            return shrinkableResult;
        }
    }

    // Step 3: Widget doesn't fit at target position - find best clamp or shrink option
    // Clamp = full-size widget shifted to fit within rect (preferred)
    // Shrink = widget reduced in size to fit within rect
    let bestClampRect: FreeRect | null = null;
    let bestClampX = 0;
    let bestClampY = 0;
    let bestClampArea = 0;

    let bestShrinkRect: FreeRect | null = null;
    let bestShrinkW = 0;
    let bestShrinkH = 0;
    let bestShrinkArea = 0;

    for (const rect of rectsUnderCursor) {
        // Only consider rects >= MIN_SIZE
        if (rect.w < MIN_SIZE - EPSILON || rect.h < MIN_SIZE - EPSILON) {
            continue;
        }

        // Calculate shrunk size (clamped to rect dimensions and widget size, snapped to grid)
        const shrunkW = snapToMosaicGrid(Math.max(MIN_SIZE, Math.min(rect.w, w)));
        const shrunkH = snapToMosaicGrid(Math.max(MIN_SIZE, Math.min(rect.h, h)));

        // Check if rect is big enough for the full widget (no shrinking needed)
        const requiresShrink = shrunkW < w - EPSILON || shrunkH < h - EPSILON;
        if (!requiresShrink) {
            // Rect is big enough for the full widget — clamp position within it
            const clamped = clampToFreeRect(targetX, targetY, w, h, rect);
            // Copy immediately — clampToFreeRect returns a shared mutable object
            const clampedX = snapToMosaicGrid(clamped.x);
            const clampedY = snapToMosaicGrid(clamped.y);
            const area = rect.w * rect.h;
            if (area > bestClampArea) {
                bestClampArea = area;
                bestClampRect = rect;
                bestClampX = clampedX;
                bestClampY = clampedY;
            }
            continue;
        }

        const area = shrunkW * shrunkH;
        if (area > bestShrinkArea) {
            bestShrinkArea = area;
            bestShrinkRect = rect;
            bestShrinkW = shrunkW;
            bestShrinkH = shrunkH;
        }
    }

    // Prefer clamped (full-size) placement over shrunk placement
    if (bestClampRect) {
        shrinkableResult.x = bestClampX;
        shrinkableResult.y = bestClampY;
        shrinkableResult.w = w;
        shrinkableResult.h = h;
        shrinkableResult.shrunk = false;
        shrinkableResult.clamped = true;
        return shrinkableResult;
    }

    if (bestShrinkRect) {
        // Position the shrunk widget maintaining proportional grab offset, snapped to grid
        // Calculate where cursor is relative to widget (as a ratio 0-1)
        const grabOffsetX = mouseX - targetX;
        const grabOffsetY = mouseY - targetY;
        const grabRatioX = Math.max(0, Math.min(1, grabOffsetX / w));
        const grabRatioY = Math.max(0, Math.min(1, grabOffsetY / h));

        // Scale the offset proportionally to the shrunk size
        const scaledOffsetX = grabRatioX * bestShrinkW;
        const scaledOffsetY = grabRatioY * bestShrinkH;

        // Position shrunk widget so cursor stays at the same proportional position
        const rawX = Math.max(
            bestShrinkRect.x,
            Math.min(mouseX - scaledOffsetX, bestShrinkRect.x + bestShrinkRect.w - bestShrinkW),
        );
        const rawY = Math.max(
            bestShrinkRect.y,
            Math.min(mouseY - scaledOffsetY, bestShrinkRect.y + bestShrinkRect.h - bestShrinkH),
        );

        shrinkableResult.x = snapToMosaicGrid(rawX);
        shrinkableResult.y = snapToMosaicGrid(rawY);
        shrinkableResult.w = bestShrinkW;
        shrinkableResult.h = bestShrinkH;
        shrinkableResult.shrunk = true;
        shrinkableResult.clamped = false;
        return shrinkableResult;
    }

    // Step 4: No valid option - blocked
    return null;
}
